import { dataService } from '../apis/dataService';
import type { 
  CashflowStatement, 
  IncomeStatement, 
  BalanceSheet, 
  FinancialDataQueryParams 
} from '../../types/api';
import cashflowLayout from '../../financialLayouts/cashflowLayout.json';
import incomeStatementLayout from '../../financialLayouts/incomeStatementLayout.json';
import balanceSheetLayout from '../../financialLayouts/balanceSheetLayout.json';

type StatementType = 'cashflow' | 'income' | 'balance';

type StatementData = CashflowStatement | IncomeStatement | BalanceSheet;

interface CategoryNode {
  properties: Array<{
    category_code: number;
    parent_code: number;
    has_children: number;
    highlight: number;
  }>;
  children?: Record<string, CategoryNode>;
}

type LayoutData = Record<string, CategoryNode>;

const layouts: Record<StatementType, LayoutData> = {
  cashflow: cashflowLayout as LayoutData,
  income: incomeStatementLayout as LayoutData,
  balance: balanceSheetLayout as LayoutData
};

/**
 * Extract all category codes from a layout recursively
 */
function extractCategoryCodes(data: LayoutData, codes: Set<number> = new Set()): Set<number> {
  for (const node of Object.values(data)) {
    const props = node.properties[0];
    if (props) {
      codes.add(props.category_code);
    }
    if (node.children) {
      extractCategoryCodes(node.children, codes);
    }
  }
  return codes;
}

/**
 * Get category codes for a specific statement type
 */
function getCategoryCodesForStatement(statementType: StatementType): Set<number> {
  const layout = layouts[statementType];
  return extractCategoryCodes(layout);
}

/**
 * Normalize API date to YYYY-MM-DD so grouping is consistent (handles "2024-01-15T00:00:00" etc.)
 */
function normalizeCalendarDateKey(calendarDate: string): string {
  if (!calendarDate || typeof calendarDate !== 'string') return calendarDate;
  if (calendarDate.length >= 10 && calendarDate.includes('-')) {
    return calendarDate.slice(0, 10);
  }
  if (calendarDate.length === 6 && /^\d{6}$/.test(calendarDate)) {
    return `${calendarDate.slice(0, 4)}-${calendarDate.slice(4, 6)}-01`;
  }
  return calendarDate;
}

/**
 * Group statement data by category_code and calendar_date
 * Returns a map: category_code -> Map<calendar_date, total_value>
 * @param data - Array of statement data
 * @param valueMode - Which value to use: 'category' for category_value, 'portioned' for portioned_value
 */
function groupStatementDataByCategoryAndDate(
  data: StatementData[],
  valueMode: 'category' | 'portioned' = 'portioned'
): Map<number, Map<string, number>> {
  const grouped = new Map<number, Map<string, number>>();

  for (const item of data) {
    const { category_code, calendar_date, category_value } = item;

    const dateKey = normalizeCalendarDateKey(calendar_date);

    // Choose value based on mode
    let value: number;
    if (valueMode === 'portioned' && 'portioned_value' in item && item.portioned_value !== undefined) {
      value = item.portioned_value;
    } else {
      value = category_value;
    }

    if (!grouped.has(category_code)) {
      grouped.set(category_code, new Map<string, number>());
    }

    const dateMap = grouped.get(category_code)!;
    dateMap.set(dateKey, (dateMap.get(dateKey) || 0) + value);
  }

  return grouped;
}

/**
 * Check if a date map has any meaningful values (non-zero or non-empty)
 * @param dateMap - Map of date strings to numeric values
 * @returns true if the map has values and at least one non-zero value
 */
function hasMeaningfulValues(dateMap: Map<string, number>): boolean {
  if (!dateMap || dateMap.size === 0) {
    return false;
  }
  
  // Check if at least one value is non-zero
  for (const value of dateMap.values()) {
    if (value !== 0) {
      return true;
    }
  }
  
  return false;
}

function sumCategoryValuesByDate(
  categoryCodes: number[],
  categoryData: Map<number, Map<string, number>>
): Map<string, number> {
  const totalsByDate = new Map<string, number>();

  for (const categoryCode of categoryCodes) {
    const valuesByDate = categoryData.get(categoryCode);
    if (!valuesByDate) continue;

    for (const [date, value] of valuesByDate) {
      totalsByDate.set(date, (totalsByDate.get(date) || 0) + value);
    }
  }

  return totalsByDate;
}

function getDirectChildCategoryCodes(children: LayoutData): number[] {
  return Object.values(children)
    .map((child) => child.properties[0]?.category_code)
    .filter((categoryCode): categoryCode is number => categoryCode !== undefined);
}

/**
 * Calculate each parent from its immediate children only.
 * Nested parent values already contain their descendants, preventing double-counting.
 * Falls back to backend data if children have no values
 */
function calculateParentCategoryValues(
  layout: LayoutData,
  categoryData: Map<number, Map<string, number>>
): Map<number, Map<string, number>> {
  const result = new Map<number, Map<string, number>>();

  for (const node of Object.values(layout)) {
    const props = node.properties[0];
    if (!props) continue;

    const categoryCode = props.category_code;

    if (props.has_children === 1 && node.children) {
      const calculatedChildValues = calculateParentCategoryValues(node.children, categoryData);
      const directChildCodes = getDirectChildCategoryCodes(node.children);
      const parentDateMap = sumCategoryValuesByDate(directChildCodes, calculatedChildValues);
      
      // Fallback: If children have no meaningful values, try backend data for parent
      if (!hasMeaningfulValues(parentDateMap)) {
        const backendValues = categoryData.get(categoryCode);
        if (backendValues && hasMeaningfulValues(backendValues)) {
          // Use backend data instead
          result.set(categoryCode, new Map(backendValues));
        } else {
          // Still set the empty/zero map
          result.set(categoryCode, parentDateMap);
        }
      } else {
        // Use calculated values from children
        result.set(categoryCode, parentDateMap);
      }
      
      // Also add child values to result
      for (const [childCode, childDateMap] of calculatedChildValues.entries()) {
        result.set(childCode, childDateMap);
      }
    } else {
      // This is a leaf category - use direct values from API
      const directValues = categoryData.get(categoryCode);
      if (directValues) {
        result.set(categoryCode, new Map(directValues));
      }
    }
  }

  return result;
}

/**
 * Fetch and calculate statement data for a specific statement type
 * @param statementType - Type of statement (cashflow, income, or balance)
 * @param user_id_no - User ID number
 * @param unit_id_no - Unit ID number
 * @param valueMode - Which value to use: 'category' for category_value, 'portioned' for portioned_value
 * @returns Promise with calculated values organized by category_code and calendar_date
 */
export async function fetchStatementData(
  statementType: StatementType,
  user_id_no: number,
  unit_id_no: number,
  valueMode: 'category' | 'portioned' = 'portioned'
): Promise<Map<number, Map<string, number>>> {
  try {
    const params: FinancialDataQueryParams = {
      user_id_no,
      unit_id_no
    };

    // Fetch data based on statement type
    let rawData: StatementData[];
    switch (statementType) {
      case 'cashflow':
        rawData = await dataService.getCashflow(params);
        break;
      case 'income':
        rawData = await dataService.getIncomeStatement(params);
        break;
      case 'balance':
        rawData = await dataService.getBalanceSheet(params);
        break;
      default:
        throw new Error(`Unknown statement type: ${statementType}`);
    }

    if (!rawData || rawData.length === 0) {
      return new Map();
    }

    // Get category codes for this statement type
    const categoryCodes = getCategoryCodesForStatement(statementType);
    
    // Filter data to only include relevant category codes
    const filteredData = rawData.filter(item => categoryCodes.has(item.category_code));

    // Group data by category_code and calendar_date, using the specified valueMode
    const groupedData = groupStatementDataByCategoryAndDate(filteredData, valueMode);

    // Get layout for this statement type
    const layout = layouts[statementType];

    // Calculate parent category values by summing children
    const calculatedData = calculateParentCategoryValues(layout, groupedData);

    return calculatedData;
  } catch (error) {
    console.error(`Error fetching ${statementType} statement data:`, error);
    throw error;
  }
}

/**
 * Get statement data formatted for the Statement component
 * Returns values organized by category path and date
 */
export async function getStatementDataForComponent(
  statementType: StatementType,
  user_id_no: number,
  unit_id_no: number
): Promise<Map<string, Map<string, number>>> {
  const categoryData = await fetchStatementData(statementType, user_id_no, unit_id_no);
  
  // Convert category_code map to category path map
  const layout = layouts[statementType];
  const pathToCodeMap = new Map<string, number>();
  
  function buildPathMap(data: LayoutData, parentPath: string = ''): void {
    for (const [categoryName, node] of Object.entries(data)) {
      const props = node.properties[0];
      if (!props) continue;
      
      const categoryPath = parentPath ? `${parentPath}::${categoryName}` : categoryName;
      pathToCodeMap.set(categoryPath, props.category_code);
      
      if (node.children) {
        buildPathMap(node.children, categoryPath);
      }
    }
  }
  
  buildPathMap(layout);
  
  // Create result map: categoryPath -> Map<date, value>
  const result = new Map<string, Map<string, number>>();
  
  for (const [path, code] of pathToCodeMap.entries()) {
    const dateMap = categoryData.get(code);
    if (dateMap) {
      result.set(path, new Map(dateMap));
    }
  }
  
  return result;
}

/**
 * Extract year from date string (handles YYYYMM and YYYY-MM-DD formats)
 */
function extractYearFromDate(dateStr: string): string {
  // Handle YYYYMM format
  if (dateStr.length === 6 && /^\d{6}$/.test(dateStr)) {
    return dateStr.substring(0, 4);
  }
  
  // Handle YYYY-MM-DD format
  if (dateStr.length === 10 && dateStr.includes('-')) {
    return dateStr.split('-')[0];
  }
  
  // If already a year (4 digits), return as is
  if (dateStr.length === 4 && /^\d{4}$/.test(dateStr)) {
    return dateStr;
  }
  
  return dateStr;
}

/**
 * Extract all available years from statement data
 * @param statementData - Map of category_code -> Map<date, value>
 * @returns Array of unique years sorted chronologically
 */
export function extractAvailablePeriods(
  statementData: Map<number, Map<string, number>>
): string[] {
  const allYears = new Set<string>();
  
  for (const dateMap of statementData.values()) {
    for (const date of dateMap.keys()) {
      const year = extractYearFromDate(date);
      allYears.add(year);
    }
  }
  
  return Array.from(allYears).sort();
}

/**
 * Filter statement data by a specific year
 * @param statementData - Map of category_code -> Map<date, value>
 * @param selectedPeriod - The year string to filter by (e.g., "2018") or 'all' for all periods
 * @returns Filtered statement data containing only dates from the selected year
 */
export function filterStatementDataByPeriod(
  statementData: Map<number, Map<string, number>>,
  selectedPeriod: string
): Map<number, Map<string, number>> {
  if (!selectedPeriod || selectedPeriod === 'all') {
    return statementData;
  }
  
  const filtered = new Map<number, Map<string, number>>();
  
  for (const [categoryCode, dateMap] of statementData.entries()) {
    const filteredDateMap = new Map<string, number>();
    
    // Filter dates that belong to the selected year
    for (const [date, value] of dateMap.entries()) {
      const year = extractYearFromDate(date);
      if (year === selectedPeriod) {
        filteredDateMap.set(date, value);
      }
    }
    
    if (filteredDateMap.size > 0) {
      filtered.set(categoryCode, filteredDateMap);
    }
  }
  
  return filtered;
}

/**
 * Get line chart data for a specific category code from statement data
 * @param statementData - Map of category_code -> Map<date, value>
 * @param categoryCode - The category code to extract data for
 * @returns Array of data points formatted for SimpleLineChart
 */
export function getLineChartDataForCategory(
  statementData: Map<number, Map<string, number>>,
  categoryCode: number
): Array<{ x: string; y: number }> {
  const categoryData = statementData.get(categoryCode);
  if (!categoryData || categoryData.size === 0) {
    return [];
  }

  // Format date from YYYYMM or YYYY-MM-DD to "MMM YYYY" format
  const formatDateToMonthYear = (dateStr: string): string => {
    if (dateStr.length === 6 && /^\d{6}$/.test(dateStr)) {
      const year = dateStr.substring(0, 4);
      const month = parseInt(dateStr.substring(4, 6), 10);
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[month - 1]} ${year}`;
    }
    if (dateStr.length === 10 && dateStr.includes('-')) {
      const [year, month] = dateStr.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[parseInt(month, 10) - 1]} ${year}`;
    }
    return dateStr;
  };

  // Convert to array and sort by date
  const dataPoints: Array<{ x: string; y: number }> = [];
  for (const [date, value] of categoryData.entries()) {
    dataPoints.push({
      x: formatDateToMonthYear(date),
      y: value
    });
  }

  // Sort by date (need to parse dates for proper sorting)
  dataPoints.sort((a, b) => {
    // Extract year and month from formatted string "MMM YYYY"
    const parseDate = (str: string): number => {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const parts = str.split(' ');
      if (parts.length === 2) {
        const month = monthNames.indexOf(parts[0]);
        const year = parseInt(parts[1], 10);
        return year * 12 + month;
      }
      return 0;
    };
    return parseDate(a.x) - parseDate(b.x);
  });

  return dataPoints;
}

/**
 * Get bar chart data for a specific category code from statement data
 * @param statementData - Map of category_code -> Map<date, value>
 * @param categoryCode - The category code to extract data for
 * @returns Array of data points formatted for HierarchicalBarChart
 */
export function getBarChartDataForCategory(
  statementData: Map<number, Map<string, number>>,
  categoryCode: number
): Array<{ name: string; value: number; color?: string }> {
  const categoryData = statementData.get(categoryCode);
  if (!categoryData || categoryData.size === 0) {
    return [];
  }

  // Format date from YYYYMM or YYYY-MM-DD to "MMM YYYY" format
  const formatDateToMonthYear = (dateStr: string): string => {
    if (dateStr.length === 6 && /^\d{6}$/.test(dateStr)) {
      const year = dateStr.substring(0, 4);
      const month = parseInt(dateStr.substring(4, 6), 10);
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[month - 1]} ${year}`;
    }
    if (dateStr.length === 10 && dateStr.includes('-')) {
      const [year, month] = dateStr.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[parseInt(month, 10) - 1]} ${year}`;
    }
    return dateStr;
  };

  // Convert to array and sort by date
  const dataPoints: Array<{ name: string; value: number; color?: string }> = [];
  for (const [date, value] of categoryData.entries()) {
    dataPoints.push({
      name: formatDateToMonthYear(date),
      value: value
    });
  }

  // Sort by date (need to parse dates for proper sorting)
  dataPoints.sort((a, b) => {
    // Extract year and month from formatted string "MMM YYYY"
    const parseDate = (str: string): number => {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const parts = str.split(' ');
      if (parts.length === 2) {
        const month = monthNames.indexOf(parts[0]);
        const year = parseInt(parts[1], 10);
        return year * 12 + month;
      }
      return 0;
    };
    return parseDate(a.name) - parseDate(b.name);
  });

  return dataPoints;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDateToMonthYearLabel(dateStr: string): string {
  if (dateStr.length === 6 && /^\d{6}$/.test(dateStr)) {
    const year = dateStr.substring(0, 4);
    const month = parseInt(dateStr.substring(4, 6), 10);
    return `${MONTH_NAMES[month - 1]} ${year}`;
  }
  if (dateStr.length === 10 && dateStr.includes('-')) {
    const [year, month] = dateStr.split('-');
    return `${MONTH_NAMES[parseInt(month, 10) - 1]} ${year}`;
  }
  return dateStr;
}

function sortDateStrings(dates: string[]): string[] {
  return [...dates].sort((a, b) => {
    const toNum = (d: string): number => {
      if (d.length === 6 && /^\d{6}$/.test(d)) return parseInt(d, 10);
      if (d.length >= 10 && d.includes('-')) return parseInt(d.replace(/-/g, '').slice(0, 8), 10);
      return 0;
    };
    return toNum(a) - toNum(b);
  });
}

function getSortedDatesAndLabels(statementData: Map<number, Map<string, number>>): { sortedDates: string[]; xLabels: string[] } {
  const allDates = new Set<string>();
  for (const dateMap of statementData.values()) {
    for (const date of dateMap.keys()) allDates.add(date);
  }
  const sortedDates = sortDateStrings(Array.from(allDates));
  const xLabels = sortedDates.map(formatDateToMonthYearLabel);
  return { sortedDates, xLabels };
}

function getValuesForDates(dateMap: Map<string, number> | undefined, sortedDates: string[]): number[] {
  if (!dateMap) return sortedDates.map(() => 0);
  return sortedDates.map((d) => dateMap.get(d) ?? 0);
}

/** Income statement: first-level income categories for stacked bars (Rental, Other Income) and NOI line */
const INCOME_NOI_BAR_CATEGORIES: { code: number; label: string; color: string }[] = [
  { code: 2, label: 'Rental Income', color: 'success' },
  { code: 3, label: 'Other Income', color: 'tertiary' }
];
const INCOME_NOI_LINE_CATEGORY = { code: 24, label: 'Net Operating Income', color: 'info' };

/** Income statement: first-level operating expense categories (children of category 4) */
const INCOME_OPEX_CATEGORIES: { code: number; label: string; color: string }[] = [
  { code: 5, label: 'Administrative Fees', color: 'warning' },
  { code: 9, label: 'Upkeep', color: 'error' },
  { code: 15, label: 'Utilities', color: 'quaternary' },
  { code: 20, label: 'Miscellaneous Expenses', color: 'secondary' },
  { code: 21, label: 'Depreciation & Write Downs', color: 'info' }
];

export interface IncomeNOIChartData {
  xLabels: string[];
  stacks: Array<{ label: string; values: number[]; color: string }>;
  lineSeries: { label: string; values: number[]; color: string };
}

export interface IncomeOpExChartData {
  xLabels: string[];
  stacks: Array<{ label: string; values: number[]; color: string }>;
}

/**
 * Build chart data for Income Statement NOI chart: stacked bars (Rental + Other Income) + overlay line (Net Operating Income).
 * Returns empty structure (xLabels: [], etc.) when no dates so the chart can show its empty state.
 */
export function getIncomeNOIChartData(
  statementData: Map<number, Map<string, number>>
): IncomeNOIChartData {
  const { sortedDates, xLabels } = getSortedDatesAndLabels(statementData);

  const stacks = INCOME_NOI_BAR_CATEGORIES.map(({ code, label, color }) => ({
    label,
    values: getValuesForDates(statementData.get(code), sortedDates),
    color
  }));

  const lineSeries = {
    label: INCOME_NOI_LINE_CATEGORY.label,
    values: getValuesForDates(statementData.get(INCOME_NOI_LINE_CATEGORY.code), sortedDates),
    color: INCOME_NOI_LINE_CATEGORY.color
  };

  return { xLabels, stacks, lineSeries };
}

/**
 * Build chart data for Income Statement Operating Expenses: stacked bars at first children level (5, 9, 15, 20, 21).
 * Returns empty structure when no dates so the chart can show its empty state.
 */
export function getIncomeOperatingExpensesChartData(
  statementData: Map<number, Map<string, number>>
): IncomeOpExChartData {
  const { sortedDates, xLabels } = getSortedDatesAndLabels(statementData);

  const stacks = INCOME_OPEX_CATEGORIES.map(({ code, label, color }) => ({
    label,
    values: getValuesForDates(statementData.get(code), sortedDates).map((v) => Math.abs(v)),
    color
  }));

  return { xLabels, stacks };
}

